import { useId } from 'react';

import { Checkbox } from '../../../components/checkbox/Checkbox';
import { useCarrierTerms } from '../../settings/models';
import { useTranslation } from '../../../contexts/translation-context';

export interface ICarrierTermsCheckboxProps {
  /** Whether this carrier has to accept the terms: only carriers we self-bill. */
  isRequired: boolean;
  isChecked: boolean;
  onChange: (isChecked: boolean) => void;
  isDisabled?: boolean;
  className?: string;
}

/** The terms document for the carrier's language; the server falls back when it is missing. */
function useCarrierTermsDocument(isRequired: boolean) {
  const { lang } = useTranslation();
  const { data, isLoading } = useCarrierTerms(isRequired ? lang : null);
  return { terms: data, isLoading };
}

/**
 * The carrier's explicit agreement to the general terms & conditions (incl. the
 * self-billing procedure), with the terms themselves one click away. Gate the
 * confirm/accept action on `isChecked` when `isRequired` — the server rejects a
 * confirmation without it either way. Renders nothing when not required.
 */
export const CarrierTermsCheckbox: React.FC<ICarrierTermsCheckboxProps> = (props) => {
  const { isRequired, isChecked, onChange, isDisabled, className } = props;
  const { i18n } = useTranslation();
  // Not tickable before the terms are in, so the carrier can open what they accept.
  const { terms, isLoading } = useCarrierTermsDocument(isRequired);
  const checkboxId = useId();

  if (!isRequired) {
    return null;
  }

  // The link sits inside the sentence, whose word order differs per language —
  // so split the translated sentence around the link text.
  const linkText = i18n('carrierTerms.termsLink');
  const sentence = i18n('carrierTerms.agreement', { terms: linkText });
  const [before, after = ''] = sentence.split(linkText);

  return (
    <div className={className ? `flex ${className}` : 'flex'}>
      <Checkbox isChecked={isChecked} onChange={onChange} id={checkboxId} isDisabled={isDisabled || isLoading} />
      {/*
       * The sentence around the link is labelled per fragment instead of wrapping
       * the whole row in one <label>: a label forwards clicks to its control, so
       * a link inside it ticks the box instead of opening the terms.
       */}
      <div className="ml-2 text-fg">
        <label htmlFor={checkboxId} className="cursor-pointer">
          {before}
        </label>
        {terms?.url ? (
          <a className="link-text" href={terms.url} target="_blank" rel="noreferrer noopener">
            {linkText}
          </a>
        ) : (
          <label htmlFor={checkboxId} className="cursor-pointer">
            {linkText}
          </label>
        )}
        <label htmlFor={checkboxId} className="cursor-pointer">
          {after}
        </label>
      </div>
    </div>
  );
};
